import type * as Api from '@ViewModels';
import { useCallback } from 'react';

import { useUserSession } from '../../../models/hooks/appStateHooks';
import { useUserPreferencesMutation } from '../../../queries/User/useUserPreferencesMutation';
import { useRealtimeCoachingEnabled } from '../../hooks/useRealtimeCoachingEnabled';
import type { UseCoachingPreferencesResult } from './types';

/**
 * Backs the focused-view Settings drawer panel.
 *
 * The auto-suggestion toggle persists via `useUserPreferencesMutation`
 * (server-side `coffeePreferences`).
 */
export function useCoachingPreferences(): UseCoachingPreferencesResult {
	const userSession = useUserSession();
	const realtimeCoachingEnabled = useRealtimeCoachingEnabled();
	const autoSuggestionsEnabled = userSession.user?.userPreferences?.coffeePreferences?.autoSuggestionsEnabled ?? false;

	const mutation = useUserPreferencesMutation({
		onSuccess: (next: Api.IUserPreferences) => {
			// Mirror the legacy queue's pattern: stamp the new prefs back onto
			// the user session so the next render sees them.
			if (userSession.user) {
				userSession.user.userPreferences = next;
			}
		},
	});

	const setAutoSuggestions = useCallback(
		async (enabled: boolean) => {
			await mutation.mutateAsync({
				preferences: {
					coffeePreferences: {
						...userSession.user?.userPreferences?.coffeePreferences,
						autoSuggestionsEnabled: enabled,
					},
				} as Api.IUserPreferences,
			});
		},
		[mutation, userSession]
	);

	return {
		realtimeCoachingEnabled,
		autoSuggestionsEnabled,
		setAutoSuggestions,
		isMutating: mutation.isPending,
	};
}
